import Vue from 'vue'
import axios from 'axios'

// Kept deliberately independent from axiosInstance/store: axiosInstance's interceptors call into
// this module, so importing axiosInstance (or anything that pulls in the store, which pulls in
// _helpers/index.js -> http.js -> axiosInstance.js) here would create a cycle.

const PING_URL = `${process.env.VUE_APP_API_URL}/monitor/status`;
const PING_TIMEOUT = 8000;
const HEARTBEAT_INTERVAL = 30000;
const OFFLINE_RETRY_INTERVAL = 3000;

export const connectionState = Vue.observable({online: true});

let probeInFlight = false;
let heartbeatTimer = null;
let offlineRetryTimer = null;
const restoreListeners = [];

function notifyRestore() {
    restoreListeners.forEach(fn => {
        try {
            fn();
        } catch (e) {
            console.log('connectivity: restore listener failed', e);
        }
    });
}

function markOnline() {
    const wasOffline = !connectionState.online;
    connectionState.online = true;
    clearOfflineRetry();
    if (wasOffline) {
        notifyRestore();
    }
}

function markOffline() {
    connectionState.online = false;
    scheduleOfflineRetry();
}

function clearOfflineRetry() {
    if (offlineRetryTimer) {
        clearTimeout(offlineRetryTimer);
        offlineRetryTimer = null;
    }
}

function scheduleOfflineRetry() {
    clearOfflineRetry();
    offlineRetryTimer = setTimeout(() => {
        ping();
    }, OFFLINE_RETRY_INTERVAL);
}

export function ping() {
    if (probeInFlight) {
        return;
    }
    probeInFlight = true;
    axios.get(PING_URL, {timeout: PING_TIMEOUT}).then(() => {
        probeInFlight = false;
        markOnline();
    }).catch(error => {
        probeInFlight = false;
        // Any response at all (even 4xx/401) means the server is up and answering.
        if (error.response && error.response.status < 500) {
            markOnline();
        } else {
            markOffline();
        }
    });
}

export function reportSuccess() {
    markOnline();
}

export function reportFailure() {
    // Don't flip state on a single failed request - it may be a cancelled navigation or an
    // unrelated timeout. Only the ping's outcome decides.
    ping();
}

function heartbeatTick() {
    if (document.hidden) {
        return;
    }
    ping();
}

export function startMonitoring() {
    if (heartbeatTimer) {
        return;
    }
    heartbeatTimer = setInterval(heartbeatTick, HEARTBEAT_INTERVAL);
    window.addEventListener('offline', () => markOffline());
    window.addEventListener('online', () => ping());
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            ping();
        }
    });
}

export function onRestore(fn) {
    restoreListeners.push(fn);
}

export function offRestore(fn) {
    const index = restoreListeners.indexOf(fn);
    if (index !== -1) {
        restoreListeners.splice(index, 1);
    }
}
